// src/pages/Admin/StatistiquesComptabilite/hooks/useComptaDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";

import { useUser } from "../../../Home/UserContext";

import { getSettings } from "../../../../services/settingsServices";
import { getAllReglementInscriptionBySaison } from "../../../../services/reglementinscriptionServices";

import {
  buildSeasonsFromActiveSeason,
  calculateComptaStats,
  getStoredUserInfo,
  getUserRoleId,
  unwrapSettings,
} from "../utils/comptaDashboardUtils";

import {
  COMPTA_SECTIONS,
  buildDefaultOpenSections,
} from "../data/comptaSections";

export function useComptaDashboard() {
  const { user } = useUser();

  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const resolvedUser = user || storedUser;
  const userRole = getUserRoleId(resolvedUser);
  const isAdmin = userRole === 1;

  const [settings, setSettings] = useState({});
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");

  const [reglements, setReglements] = useState([]);

  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingReglements, setLoadingReglements] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [openSections, setOpenSections] = useState(() =>
    buildDefaultOpenSections()
  );

  const fetchSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      setErrorMsg("");

      const response = await getSettings();
      const cleanSettings = unwrapSettings(response);
      const activeSeason = cleanSettings?.saison_active || "";

      setSettings(cleanSettings || {});

      if (activeSeason) {
        setSeasons(buildSeasonsFromActiveSeason(activeSeason));
        setSelectedSeason((current) => current || activeSeason);
      }
    } catch (error) {
      console.error("Erreur chargement paramètres compta :", error);
      setErrorMsg("Impossible de récupérer la saison active du club.");
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const fetchReglements = useCallback(async (season) => {
    if (!season) return;

    try {
      setLoadingReglements(true);
      setErrorMsg("");

      const response = await getAllReglementInscriptionBySaison(season);

      setReglements(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur chargement règlements compta :", error);
      setReglements([]);
      setErrorMsg("Impossible de récupérer les règlements de la saison.");
    } finally {
      setLoadingReglements(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (selectedSeason) {
      fetchReglements(selectedSeason);
    }
  }, [fetchReglements, selectedSeason]);

  const stats = useMemo(() => {
    return calculateComptaStats(reglements);
  }, [reglements]);

  const openedCount = useMemo(() => {
    return Object.values(openSections).filter(Boolean).length;
  }, [openSections]);

  const toggleSection = useCallback((key) => {
    setOpenSections((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  }, []);

  const openAll = useCallback(() => {
    setOpenSections(
      COMPTA_SECTIONS.reduce((acc, section) => {
        acc[section.key] = true;
        return acc;
      }, {})
    );
  }, []);

  const closeAll = useCallback(() => {
    setOpenSections(
      COMPTA_SECTIONS.reduce((acc, section) => {
        acc[section.key] = false;
        return acc;
      }, {})
    );
  }, []);

  const refresh = useCallback(async () => {
    await fetchReglements(selectedSeason);
  }, [fetchReglements, selectedSeason]);

  return {
    user: resolvedUser,
    userRole,
    isAdmin,

    settings,
    seasons,
    selectedSeason,
    setSelectedSeason,

    reglements,
    stats,

    loadingSettings,
    loadingReglements,
    loading: loadingSettings || loadingReglements,
    errorMsg,

    openSections,
    openedCount,
    toggleSection,
    openAll,
    closeAll,

    refresh,
    fetchSettings,
    fetchReglements,
  };
}